/* eslint-disable prettier/prettier */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import React from 'react'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchTierMasterDataUrl,
  addTierMasterDataUrl,
  editTierMasterDataUrl,
  uploadTierImageUrl,
  deleteTierMasterDataUrl,
} from 'src/services/urls'
import { setSearchBar } from './tableSlice'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  activePage: 0,
}

// Fetch tier master data
export const fetchTierMasterData = createAsyncThunk(
  'tierMasterData/fetchTierMasterData',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const tierName = options?.tierName
      const brandName = options?.brandName
      const sortBy = options?.sortBy
      const orderBy = options?.orderBy

      const response = await makeRequest(
        fetchTierMasterDataUrl(skip, limit, tierName, brandName, sortBy, orderBy),
      )
      return { docs: response.data.data.docs, data: response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

// Add a tier master data manually - no upload widget
export const addTierMasterData = createAsyncThunk(
  'tierMasterData/addTierMasterData',
  async (data, { dispatch }) => {
    try {
      const body = { ...data }
      const searchTierName = body?.searchTierName
      delete body.searchTierName
      delete body._id
      const response = await makeRequest(addTierMasterDataUrl(), 'POST', body)
      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      dispatch(fetchTierMasterData({ tierName: searchTierName }))
      dispatch(setSearchBar(searchTierName))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Edit/Update or enable/disable a tier master data
export const editTierMasterData = createAsyncThunk(
  'tierMasterData/editTierMasterData',
  async (data, { dispatch }) => {
    try {
      let body = { ...data }
      const searchTierName = body?.searchTierName
      delete body.searchTierName
      delete body._id
      const id = data._id

      const response = await makeRequest(editTierMasterDataUrl(id), 'PUT', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchTierMasterData({ tierName: searchTierName }))
      dispatch(setSearchBar(searchTierName))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Delete a tier master data
export const deleteTierMasterData = createAsyncThunk(
  'tierMasterData/deleteTierMasterData',
  async (id, { dispatch }) => {
    try {
      const response = await makeRequest(deleteTierMasterDataUrl(id), 'DELETE')
      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }

      dispatch(fetchTierMasterData())

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const uploadTierImage = createAsyncThunk(
  'tierMasterData/uploadTierImage',
  async (data, { dispatch }) => {
    try {
      const formData = new FormData()
      formData.append('id', data.id)
      formData.append('module', data.module)
      formData.append('file', data.file)
      const response = await makeRequest(uploadTierImageUrl(), 'POST', formData)
      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        dispatch(fetchTierMasterData())
      }
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const tierMasterDataSlice = createSlice({
  name: 'tierMasterData',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTierMasterData.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchTierMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.data = action.payload.data
        state.docs = action.payload.docs
      }
    })
    builder.addCase(fetchTierMasterData.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(addTierMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addTierMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editTierMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editTierMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(deleteTierMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(deleteTierMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })

    builder.addCase(uploadTierImage.fulfilled, (state, action) => {
      state.loading = false
      // state.data = action.payload
      state.error = ''
    })
    builder.addCase(uploadTierImage.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
  },
})

export default tierMasterDataSlice.reducer
export const { setActivePage } = tierMasterDataSlice.actions
